import React, { useEffect, useRef, useState } from 'react';
import { getDocument, GlobalWorkerOptions } from 'pdfjs-dist';
import SignaturePad from 'signature_pad';
import * as PDFLib from 'pdf-lib';
import { requester, fileRequester } from 'App';

GlobalWorkerOptions.workerSrc = `https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js`;

// 계약자 정보 상수
const CONTRACTOR = {
    name: '푸푸 오토스',
};

const PdfSigner = () => {
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [pdfBytes, setPdfBytes] = useState(null);
    const [pageCount, setPageCount] = useState(0);
    const [pdfLoaded, setPdfLoaded] = useState(false);
    const [clientName, setClientName] = useState('');
    const [sealImage, setSealImage] = useState(null);
    const [isLoading, setIsLoading] = useState(false);

    const clientSigCanvasRef = useRef(null);
    const clientSigPad = useRef(null);
    const canvasRefs = useRef([]);
    const sealInputRef = useRef(null);

    // 서명 패드 초기화
    useEffect(() => {
        if (clientSigCanvasRef.current && !clientSigPad.current) {
            clientSigPad.current = new SignaturePad(clientSigCanvasRef.current);
        }
    }, []);

    // 활성화된 템플릿 불러오기
    useEffect(() => {
        setIsLoading(true);
        requester.getAllContractTemplates((response) => {
            setTemplates(response.data || []);
            setIsLoading(false);
        });
    }, []);

    // 템플릿 선택 시 PDF 불러오기
    const handleTemplateSelect = (event) => {
        const templateId = event.target.value;
        if (!templateId) return;

        setIsLoading(true);
        // canvas 참조 초기화
        canvasRefs.current = [];
        setPdfLoaded(false);

        // 응답 타입을 명시적으로 지정하여 PDF 파일 요청
        fileRequester.getContractTemplatePdf(templateId, async (response, error) => {
            // 디버깅: 응답 상태 확인
            console.log('PDF 응답 데이터 타입:', typeof response);
            console.log('PDF 응답 데이터 길이:', response ? response.byteLength : 'N/A');

            if (error || !response) {
                console.error('PDF 로드 실패:', error);
                alert('PDF를 불러오는 중 오류가 발생했습니다.');
                setIsLoading(false);
                return;
            }

            try {
                // 주의: response가 ArrayBuffer인지 확인
                const uint8Array = new Uint8Array(response);
                console.log('PDF 데이터 크기:', uint8Array.length);

                // 데이터가 비어있는지 확인
                if (uint8Array.length === 0) {
                    throw new Error('PDF 데이터가 비어 있습니다.');
                }

                setPdfBytes(uint8Array);

                // PDF.js를 사용하여 PDF 로드
                const loadingTask = getDocument({ data: uint8Array.slice(0) });

                // 진행 상황 추적 (선택사항)
                loadingTask.onProgress = (progress) => {
                    console.log(`PDF 로딩 진행률: ${progress.loaded / progress.total * 100}%`);
                };

                const pdf = await loadingTask.promise;
                setPageCount(pdf.numPages);
                setPdfLoaded(true);
                setSelectedTemplate(templateId);
            } catch (err) {
                console.error('PDF 파싱 오류:', err);
                alert(`PDF 파일 파싱 중 오류가 발생했습니다: ${err.message}`);
            } finally {
                setIsLoading(false);
            }
        });
    };

    // PDF 렌더링 useEffect
    useEffect(() => {
        const renderPages = async () => {
            if (!pdfBytes || !pdfLoaded || canvasRefs.current.length === 0) return;

            const pdf = await getDocument({ data: pdfBytes.slice(0) }).promise;

            for (let i = 1; i <= pdf.numPages; i++) {
                const page = await pdf.getPage(i);
                const viewport = page.getViewport({ scale: 1.5 });

                const canvas = canvasRefs.current[i - 1]?.current;
                if (!canvas) continue;
                const context = canvas.getContext('2d');
                canvas.width = viewport.width;
                canvas.height = viewport.height;

                await page.render({ canvasContext: context, viewport }).promise;
            }
        };

        renderPages();
    }, [pdfBytes, pdfLoaded]);

    // 도장 이미지 업로드 핸들러
    const handleSealUpload = (event) => {
        const file = event.target.files[0];
        if (file) {
            const reader = new FileReader();
            reader.onload = (e) => {
                setSealImage(e.target.result);
            };
            reader.readAsDataURL(file);
        }
    };

    // 텍스트 이미지 생성 헬퍼 함수
    const createTextImage = async (pdfDoc, text) => {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = 400;
        canvas.height = 60;

        // 배경
        ctx.fillStyle = 'white';
        ctx.fillRect(0, 0, canvas.width, canvas.height);

        // 텍스트 스타일
        ctx.fillStyle = 'black';
        ctx.font = 'bold 18px Noto Sans KR';
        ctx.textBaseline = 'middle';
        ctx.textAlign = 'center';
        ctx.fillText(text, canvas.width / 2, canvas.height / 2);

        const dataUrl = canvas.toDataURL('image/png');
        const textImgBytes = await fetch(dataUrl).then(res => res.arrayBuffer());
        return await pdfDoc.embedPng(textImgBytes);
    };

    // PDF 서명 저장
    const saveSignedPdf = async () => {
        if (!pdfBytes || !selectedTemplate) {
            alert('계약서 템플릿을 선택해주세요.');
            return;
        }

        if (clientSigPad.current.isEmpty()) {
            alert('판매자 서명을 입력해주세요.');
            return;
        }

        if (!clientName) {
            alert('판매자 성명을 입력해주세요.');
            return;
        }

        if (!sealImage) {
            alert('도장 이미지를 업로드해주세요.');
            return;
        }

        setIsLoading(true);

        try {
            const today = new Date().toISOString().split('T')[0].replace(/-/g, '.');

            const pdfDoc = await PDFLib.PDFDocument.load(pdfBytes.slice(0));
            const pages = pdfDoc.getPages();
            const lastPage = pages[pages.length - 1];

            // 도장 이미지 임베딩 (JPG 또는 PNG 지원)
            const sealImgBytes = await fetch(sealImage).then(res => res.arrayBuffer());

            // 파일 형식에 따라 적절한 임베딩 함수 사용
            let sealImagePdf;
            const imageType = getImageTypeFromDataURL(sealImage);

            if (imageType === 'jpg' || imageType === 'jpeg') {
                sealImagePdf = await pdfDoc.embedJpg(sealImgBytes);
            } else {
                sealImagePdf = await pdfDoc.embedPng(sealImgBytes);
            }

            // 계약자(사업자) 텍스트 이미지
            const contractorTextImage = await createTextImage(pdfDoc, `사업자: ${CONTRACTOR.name} / ${today}`);

            // 판매자 서명 이미지
            const clientSigDataUrl = clientSigPad.current.toDataURL();
            const clientSigImgBytes = await fetch(clientSigDataUrl).then(res => res.arrayBuffer());
            const clientSigImage = await pdfDoc.embedPng(clientSigImgBytes);

            // 판매자 텍스트 이미지
            const clientTextImage = await createTextImage(pdfDoc, `판매자: ${clientName} / ${today}`);

            // 서명 영역 배경
            const x = 50;
            const y = 80;
            const width = 500;
            const height = 150;

            // 서명 영역 테두리
            lastPage.drawRectangle({
                x,
                y,
                width,
                height,
                borderColor: PDFLib.rgb(0.8, 0.8, 0.8),
                borderWidth: 1,
            });

            // 계약자 도장 (왼쪽)
            lastPage.drawImage(sealImagePdf, {
                x: x + 20,
                y: y + 20,
                width: 100,
                height: 100,
            });

            lastPage.drawImage(contractorTextImage, {
                x: x + 20,
                y: y + 110,
                width: 200,
                height: 30,
            });

            // 판매자 서명 (오른쪽)
            lastPage.drawImage(clientSigImage, {
                x: x + 280,
                y: y + 20,
                width: 200,
                height: 80,
            });

            lastPage.drawImage(clientTextImage, {
                x: x + 280,
                y: y + 110,
                width: 200,
                height: 30,
            });

            // PDF 저장 및 서버 업로드
            const signedPdfBytes = await pdfDoc.save();
            const blob = new Blob([signedPdfBytes], { type: 'application/pdf' });

            const formData = new FormData();
            const requestJson = JSON.stringify({
                templateId: selectedTemplate,
                sellerName: clientName
            });
            const requestBlob = new Blob([requestJson], { type: 'application/json' });

            formData.append('request', requestBlob);
            formData.append('pdf', new File([blob], 'signed-contract.pdf', { type: 'application/pdf' }));

            // Promise를 반환하는 함수를 await로 처리
            const response = await fileRequester.uploadSignedContract(formData);
            console.log("계약서 업로드 응답:", response);

            if (response && response.code === 0) {
                alert('계약서가 성공적으로 저장되었습니다.');

                // 성공 후 폼 초기화
                clientSigPad.current.clear();
                setClientName('');
                setSealImage(null);
                if (sealInputRef.current) {
                    sealInputRef.current.value = '';
                }
            } else {
                console.error('예상치 못한 응답:', response);
                alert('계약서 저장 중 문제가 발생했습니다.');
            }
        } catch (error) {
            console.error('PDF 저장 중 오류 발생:', error);
            alert('PDF 저장 중 오류가 발생했습니다.');
        } finally {
            // 항상 로딩 상태 해제
            setIsLoading(false);
        }
    };

    // 데이터 URL에서 이미지 타입을 추출하는 함수
    const getImageTypeFromDataURL = (dataURL) => {
        if (!dataURL) return 'png';

        // 데이터 URL 형식: data:image/[type];base64,...
        const match = dataURL.match(/data:image\/([a-zA-Z0-9]+);/);
        if (match && match[1]) {
            return match[1].toLowerCase();
        }

        // 파일 확장자 확인 (URL이 파일 경로인 경우)
        if (dataURL.match(/\.(jpg|jpeg)$/i)) {
            return 'jpg';
        }
        if (dataURL.match(/\.png$/i)) {
            return 'png';
        }

        // 기본값
        return 'png';
    };

    return (
        <div style={{
            maxWidth: '1200px',
            margin: '0 auto',
            padding: '20px',
            width: '100%',
            boxSizing: 'border-box'
        }}>
            <h2 style={{ textAlign: 'center', marginBottom: '30px', color: '#333' }}>📄 계약서 작성</h2>

            {/* 템플릿 선택 */}
            <div style={{
                marginBottom: '30px',
                padding: '20px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)'
            }}>
                <label style={{ display: 'block', marginBottom: '10px', fontWeight: 'bold' }}>
                    계약서 템플릿 선택:
                </label>
                <select
                    value={selectedTemplate || ''}
                    onChange={handleTemplateSelect}
                    style={{
                        width: '100%',
                        padding: '10px',
                        border: '1px solid #ddd',
                        borderRadius: '4px',
                        fontSize: '16px'
                    }}
                >
                    <option value="">-- 템플릿 선택 --</option>
                    {templates && templates?.map(template => (
                        <option key={template?.id} value={template?.id}>
                            {template?.name} - {template?.description || '설명 없음'}
                        </option>
                    ))}
                </select>
            </div>

            {/* PDF 미리보기 */}
            {pdfLoaded && (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '20px',
                    marginBottom: '30px'
                }}>
                    <h3 style={{ textAlign: 'center', color: '#333' }}>계약서 미리보기</h3>
                    {Array.from({ length: pageCount }, (_, i) => {
                        if (!canvasRefs.current[i]) {
                            canvasRefs.current[i] = React.createRef();
                        }
                        return (
                            <canvas
                                key={i}
                                ref={canvasRefs.current[i]}
                                style={{
                                    border: '1px solid #ccc',
                                    width: '100%',
                                    height: 'auto',
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                    borderRadius: '4px'
                                }}
                            />
                        );
                    })}
                </div>
            )}

            {/* 서명 폼 */}
            <div style={{
                marginTop: '30px',
                padding: '20px',
                backgroundColor: '#f8f9fa',
                borderRadius: '8px',
                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                width: '100%',
                boxSizing: 'border-box'
            }}>
                <h3 style={{ textAlign: 'center', marginBottom: '20px', color: '#333' }}>📝 서명 정보 입력</h3>

                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '20px',
                    marginBottom: '30px'
                }}>
                    {/* 계약자 성명 */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                            사업자 :
                        </label>
                        <div style={{
                            padding: '8px 12px',
                            backgroundColor: '#f8f9fa',
                            border: '1px solid #ddd',
                            borderRadius: '4px',
                            fontSize: '16px',
                            color: '#666'
                        }}>
                            {CONTRACTOR.name}
                        </div>
                    </div>

                    {/* 도장 이미지 업로드 */}
                    <div>
                        <h3 style={{ marginBottom: '15px', color: '#333' }}>🖋️ 도장 이미지 업로드</h3>
                        <input
                            type="file"
                            accept="image/*"
                            ref={sealInputRef}
                            onChange={handleSealUpload}
                            style={{
                                width: '100%',
                                padding: '8px',
                                border: '1px solid #ddd',
                                borderRadius: '4px'
                            }}
                        />

                        {sealImage && (
                            <div style={{
                                marginTop: '15px',
                                border: '2px solid #ddd',
                                borderRadius: '4px',
                                padding: '10px',
                                textAlign: 'center',
                                backgroundColor: 'white',
                                maxWidth: '500px'
                            }}>
                                <img
                                    src={sealImage}
                                    alt="도장"
                                    style={{
                                        maxWidth: '200px',
                                        maxHeight: '200px',
                                        objectFit: 'contain'
                                    }}
                                />
                            </div>
                        )}
                    </div>

                    {/* 판매자 성명 */}
                    <div>
                        <label style={{ display: 'block', marginBottom: '8px', fontWeight: 'bold' }}>
                            판매자 성명 :
                        </label>
                        <input
                            type="text"
                            value={clientName}
                            onChange={e => setClientName(e.target.value)}
                            placeholder="판매자 성명"
                            style={{
                                width: '100%',
                                padding: '8px 12px',
                                border: '1px solid #ddd',
                                borderRadius: '4px',
                                fontSize: '16px',
                                boxSizing: 'border-box'
                            }}
                        />
                    </div>

                    {/* 판매자 서명 */}
                    <div>
                        <h3 style={{ marginBottom: '15px', color: '#333' }}>🖋️ 판매자 서명</h3>
                        <canvas
                            ref={clientSigCanvasRef}
                            width={500}
                            height={200}
                            style={{
                                border: '2px solid #ddd',
                                borderRadius: '4px',
                                backgroundColor: 'white',
                                boxShadow: '0 2px 4px rgba(0,0,0,0.05)',
                                width: '100%',
                                height: 'auto',
                                maxWidth: '500px'
                            }}
                        />
                        <div style={{ marginTop: '15px', display: 'flex', gap: '10px' }}>
                            <button
                                onClick={() => clientSigPad.current && clientSigPad.current.clear()}
                                style={{
                                    padding: '8px 16px',
                                    backgroundColor: '#f8f9fa',
                                    border: '1px solid #ddd',
                                    borderRadius: '4px',
                                    cursor: 'pointer',
                                    transition: 'all 0.2s'
                                }}
                                onMouseOver={e => e.target.style.backgroundColor = '#e9ecef'}
                            >
                                지우기
                            </button>
                        </div>
                    </div>

                    {/* 저장 버튼 */}
                    <div style={{ textAlign: 'center', marginTop: '20px' }}>
                        <button
                            onClick={saveSignedPdf}
                            disabled={isLoading || !selectedTemplate}
                            style={{
                                padding: '12px 24px',
                                backgroundColor: (isLoading || !selectedTemplate) ? '#cccccc' : '#007bff',
                                color: 'white',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: (isLoading || !selectedTemplate) ? 'not-allowed' : 'pointer',
                                transition: 'all 0.2s',
                                fontSize: '16px',
                                fontWeight: 'bold',
                                width: '100%',
                                maxWidth: '300px'
                            }}
                            onMouseOver={e => {
                                if (!isLoading && selectedTemplate) {
                                    e.target.style.backgroundColor = '#0056b3';
                                }
                            }}
                        >
                            {isLoading ? '처리 중...' : '서명 완료 및 저장'}
                        </button>
                    </div>

                    {/* 로딩 표시 */}
                    {isLoading && (
                        <div style={{
                            position: 'fixed',
                            top: 0,
                            left: 0,
                            width: '100%',
                            height: '100%',
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            display: 'flex',
                            justifyContent: 'center',
                            alignItems: 'center',
                            zIndex: 9999
                        }}>
                            <div style={{
                                backgroundColor: 'white',
                                padding: '20px',
                                borderRadius: '8px',
                                textAlign: 'center'
                            }}>
                                <p style={{ fontSize: '18px', fontWeight: 'bold' }}>처리 중입니다...</p>
                                <p>잠시만 기다려주세요.</p>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

};

export default PdfSigner;